import { useLingui } from '@lingui/react/macro';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DayPicker, type DayPickerProps } from 'react-day-picker';
import { enGB, es, ptBR } from 'react-day-picker/locale';

import { buttonVariants } from './button';
import { cn } from './cn';

const LOCALES = { 'pt-BR': ptBR, en: enGB, es } as const;

/** Calendário (react-day-picker 10) no Lume, no idioma ativo. Fala `Date`; datas civis passam pela ponte de `format/date`. */
export function Calendar({ className, classNames, ...props }: DayPickerProps) {
  const { i18n } = useLingui();
  const locale = LOCALES[i18n.locale as keyof typeof LOCALES];
  return (
    <DayPicker
      data-slot="calendar"
      locale={locale}
      showOutsideDays
      className={cn('p-1', className)}
      classNames={{
        months: 'relative flex flex-col gap-3',
        month: 'flex flex-col gap-3',
        month_caption: 'flex h-8 items-center justify-center',
        caption_label: 'text-sm font-medium capitalize',
        nav: 'absolute inset-x-0 top-0 flex h-8 items-center justify-between',
        button_previous: cn(buttonVariants({ variant: 'ghost', size: 'icon' }), 'size-8'),
        button_next: cn(buttonVariants({ variant: 'ghost', size: 'icon' }), 'size-8'),
        month_grid: 'w-full border-collapse',
        weekdays: 'flex',
        weekday: 'w-9 text-xs font-normal text-muted-foreground md:w-8',
        week: 'mt-1 flex w-full',
        day: 'relative size-9 p-0 text-center text-sm md:size-8',
        day_button:
          'size-9 rounded-md text-sm tabular-nums hover:bg-muted aria-selected:bg-primary aria-selected:text-primary-foreground md:size-8',
        selected: 'font-medium',
        today: 'font-semibold text-accent-text',
        outside: 'text-faint',
        disabled: 'pointer-events-none opacity-40',
        hidden: 'invisible',
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation }) =>
          orientation === 'left' ? <ChevronLeft aria-hidden className="size-4" /> : <ChevronRight aria-hidden className="size-4" />,
      }}
      {...props}
    />
  );
}
